import React, { Component } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import CatalogToolbar from './CatalogToolbar';
import CatalogPager from './CatalogPager';
import { toGridRows } from '../../../util/marineCatalogGrouping';
import { parseClasses, labelFor } from '../../../util/marineClassOptions';
import { getBoatCatalog, getBoatClasses } from '../../../services/marineReferenceService';
import { isInstallerAdmin } from '../../../util/permissions';
import MarineCraftEditModal from './MarineCraftEditModal';
import AddCraftModal from './AddCraftModal';

const PAGE_SIZE = 50;

/**
 * Water mode — the boat reference catalog admin.
 *
 * Paging and search are SERVER-side (unlike the Road grid, which loads its whole
 * catalog once): `GET /api/boats/catalog` pages by make+model GROUP so a group's
 * generations always arrive together.
 *
 * The make filter is built from the RESPONSE, never from `GET /api/boats/makes` —
 * `boat_make` holds 15,998 rows, the USCG manufacturer registry, and only 53
 * carry any model. A dropdown of the full list would be unusable and would imply
 * models exist where none do.
 */
export default class MarineCatalog extends Component {
  state = {
    groups: [],
    total: 0,
    page: 1,
    searchTerm: '',
    makeFilter: 'All Makes',
    classes: { all: [], canonical: [], needsCleanup: [] },
    loading: false,
    error: '',
    editGroup: null,
    showAdd: false,
  };

  componentDidMount() {
    this.loadClasses();
    this.loadCatalog();
  }

  loadClasses = async () => {
    try {
      this.setState({ classes: parseClasses(await getBoatClasses()) });
    } catch (error) {
      // Non-fatal: the grid still reads without the vocabulary. The modals
      // guard themselves on an empty canonical list.
      this.setState({ error: error.message });
    }
  };

  loadCatalog = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const { groups, total } = await getBoatCatalog({
        page: this.state.page,
        pageSize: PAGE_SIZE,
        q: this.state.searchTerm || undefined,
        make: this.state.makeFilter !== 'All Makes' ? this.state.makeFilter : undefined,
      });
      this.setState({ groups, total, loading: false });
    } catch (error) {
      this.setState({ error: error.message, loading: false, groups: [], total: 0 });
    }
  };

  // Search and filter both reset to page 1 — staying on page 7 of a narrowed
  // result set shows an empty grid and reads as "no matches".
  handleSearch = (e) => this.setState({ searchTerm: e.target.value, page: 1 }, this.debouncedLoad);
  handleMakeFilter = (e) => this.setState({ makeFilter: e.target.value, page: 1 }, this.loadCatalog);
  handlePage = (page) => this.setState({ page }, this.loadCatalog);

  // Typing hits the server; without this every keystroke is a request.
  debouncedLoad = () => {
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(this.loadCatalog, 350);
  };

  componentWillUnmount() { clearTimeout(this._searchTimer); }

  afterWrite = () => { this.setState({ editGroup: null, showAdd: false }, this.loadCatalog); };

  columns() {
    return [
      { dataField: 'yearRange', text: 'Years', headerStyle: { width: '120px' } },
      { dataField: 'makeName', text: 'Make', sort: true, headerStyle: { width: '160px' } },
      { dataField: 'modelName', text: 'Model', sort: true, headerStyle: { width: '180px' } },
      {
        // The distinct tiers across the group's class rows: "L", or "S, M"
        // when the classes are tiered differently, or an em dash when none.
        dataField: 'classRows',
        text: 'Tier',
        headerStyle: { width: '90px' },
        style: { textAlign: 'center' },
        formatter: (cell) => {
          const tiers = [...new Set((cell || []).map((r) => r.sizeTier).filter(Boolean))];
          return tiers.length ? tiers.join(', ') : <span className="text-muted">&mdash;</span>;
        },
      },
      {
        dataField: 'classCount',
        text: 'Classes',
        headerStyle: { width: '150px' },
        style: { textAlign: 'center' },
        formatter: (cell, row) => (
          <button className="btn btn-link text-primary p-0"
            style={{ textDecoration: 'underline', fontSize: '14px' }}
            onClick={() => this.setState({ editGroup: row.group })}
            title={row.classRows.map((r) => labelFor(this.state.classes, r.boatClass) || r.label).join(', ')}>
            {cell} class{cell !== 1 ? 'es' : ''}
          </button>
        ),
      },
    ];
  }

  render() {
    const canEdit = isInstallerAdmin(this.props.userRole);
    // Filter FIRST and reuse the same array. `toGridRows` drops falsy entries
    // internally, so mapping its output back onto the raw `groups` by index
    // would silently desync and open the wrong group in the edit modal.
    const safeGroups = this.state.groups.filter(Boolean);
    const rows = toGridRows(safeGroups).map((r, i) => ({ ...r, group: safeGroups[i] }));
    const makeOptions = ['All Makes', ...new Set(safeGroups.map((g) => g.make_name).filter(Boolean))];
    const totalPages = Math.max(1, Math.ceil(this.state.total / PAGE_SIZE));

    return (
      <React.Fragment>
        {this.state.error && <div className="alert alert-danger">{this.state.error}</div>}

        <CatalogToolbar
          searchLabel="Search Craft"
          searchPlaceholder="Search make or model..."
          searchValue={this.state.searchTerm}
          onSearchChange={this.handleSearch}
          filterLabel="Filter by Make"
          filterValue={this.state.makeFilter}
          filterOptions={makeOptions}
          onFilterChange={this.handleMakeFilter}
          actionLabel="Add New Craft"
          onAction={() => this.setState({ showAdd: true })}
          actionDisabled={this.state.loading}
          showAction={canEdit}
        />

        <div className="card">
          <div className="card-body">
            {this.state.loading ? (
              <div className="text-center py-4">
                <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x" />
                <p className="mt-2">Loading boat catalog...</p>
              </div>
            ) : (
              <React.Fragment>
                <h6 className="mb-3">
                  {this.state.total} craft group{this.state.total !== 1 ? 's' : ''}
                </h6>
                <div className="jv-bt-table">
                  <BootstrapTable keyField="id" data={rows} columns={this.columns()}
                    striped hover condensed noDataIndication="No craft found" />
                </div>
                <CatalogPager currentPage={this.state.page} totalPages={totalPages}
                  onPageChange={this.handlePage} />
              </React.Fragment>
            )}
          </div>
        </div>

        {this.state.editGroup && (
          <MarineCraftEditModal
            group={this.state.editGroup}
            classes={this.state.classes}
            canEdit={canEdit}
            onClose={() => this.setState({ editGroup: null })}
            onSaved={this.afterWrite}
          />
        )}

        {this.state.showAdd && (
          <AddCraftModal
            classes={this.state.classes}
            onClose={() => this.setState({ showAdd: false })}
            onSaved={this.afterWrite}
          />
        )}
      </React.Fragment>
    );
  }
}
